/**
 * Compositor ÚNICO do atendimento e da conversa (spec 018, US3 / FR-005):
 * dois modos EXPLÍCITOS — "Responder" (com o canal escolhido: WhatsApp quando
 * há conversa vinculada, modelo quando a janela de 24h fechou, comentário
 * público/e-mail) e "Nota interna" (âmbar, "Visível somente para a equipe",
 * "Salvar nota interna"). Um rascunho por modo (drafts.ts).
 *
 * Nenhum caminho de escrita novo (plano da 018): comentário pelo POST
 * /tickets/:n/comments, WhatsApp pelo POST /chats/:id/messages|media|template
 * — as mesmas guardas do Completo (espelhado, posse da conversa, janela, pausa).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Paperclip, SendHorizonal, X } from 'lucide-react';
import { MAX_UPLOAD_BYTES } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label, Select } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { cn } from '@/lib/utils';
import { chatDetailOptions, whatsappWindowOptions } from '@/features/chats/chats-queries';
import {
  useResumeChat,
  useSendChatMedia,
  useSendChatMessage,
  useSendChatTemplate,
  type ChatTemplateBody,
} from '@/features/chats/chats-mutations';
import { TemplatePicker } from '@/features/chats/whatsapp-template-picker';
import { useQuickReplyComposer } from '@/features/chats/quick-reply-picker';
import { errorMessage } from '@/features/tickets/ticket-format';
import { uploadTicketFile, useAddComment } from '@/features/tickets/tickets-mutations';
import { mentionErrorMessage, useMentionAutocomplete } from '@/features/tickets/mentions';
import { formatFileSize } from '@/features/knowledge/knowledge-format';
import { useAssumeWork } from '@/features/work/work-actions';
import type { ComposerMode, useDrafts } from './drafts';

export type ReplyChannel = { kind: 'comment' } | { kind: 'chat'; chatId: number };
export type NoteTarget =
  | { kind: 'ticket'; ticketNumber: number }
  | { kind: 'chat'; chatId: number }
  | { kind: 'none'; reason: string };

export const channelKey = (c: ReplyChannel) => (c.kind === 'comment' ? 'comment' : `chat:${c.chatId}`);

const MAX_LABEL = `${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB`;

const textareaClass =
  'field-sizing-content max-h-60 min-h-20 w-full resize-none rounded-md border border-input bg-card px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50';

export function Composer({
  channels,
  noteTarget,
  ticketNumber,
  requestorEmail,
  commentBlockedReason,
  defaultChannel,
  drafts,
  autoFocus,
  onSent,
  actions,
  startConversation,
}: {
  /** para onde a resposta pode ir — a ordem é a do seletor */
  channels: Array<{ channel: ReplyChannel; label: string }>;
  noteTarget: NoteTarget;
  /** para o comentário público (null = compositor de conversa sem atendimento) */
  ticketNumber: number | null;
  requestorEmail: string | null;
  /** por que o comentário público não existe aqui (ticket espelhado) — o WhatsApp segue */
  commentBlockedReason: string | null;
  defaultChannel: ReplyChannel | null;
  drafts: ReturnType<typeof useDrafts>;
  autoFocus: boolean;
  onSent: () => void;
  /** "Registrar ação" — o menu vem de quem monta */
  actions?: ReactNode;
  /** spec 057: "Nova conversa no WhatsApp", ao lado do canal (também sem canal nenhum) */
  startConversation?: ReactNode;
}) {
  const [mode, setMode] = useState<ComposerMode>('reply');
  // o canal SEGUE o padrão (onde o cliente falou por último) até a pessoa
  // escolher outro; o escolhido que sumir (conversa encerrada) volta ao padrão
  const [picked, setPicked] = useState<ReplyChannel | null>(null);
  const available = (c: ReplyChannel | null): c is ReplyChannel =>
    c !== null && channels.some((x) => channelKey(x.channel) === channelKey(c));
  const channel: ReplyChannel | null = available(picked)
    ? picked
    : available(defaultChannel)
      ? defaultChannel
      : (channels[0]?.channel ?? null);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // "Responder" do Hoje: abre com o compositor em foco no canal certo (US1 AS5)
  useEffect(() => {
    if (!autoFocus) return;
    setMode('reply');
    const id = window.setTimeout(() => replyRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [autoFocus, channel && channelKey(channel)]);

  const switchMode = (next: ComposerMode) => {
    setMode(next);
    window.setTimeout(() => (next === 'reply' ? replyRef.current : noteRef.current)?.focus(), 0);
  };

  return (
    <section
      aria-label="Compositor"
      className={cn(
        'rounded-lg border bg-card shadow-sm transition-colors',
        mode === 'note' && 'border-[#FFE58F] bg-[#FFFBE6]'
      )}
    >
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5" role="tablist" aria-label="Modo">
          <ModeTab active={mode === 'reply'} onClick={() => switchMode('reply')}>
            Responder
          </ModeTab>
          <ModeTab active={mode === 'note'} onClick={() => switchMode('note')}>
            <Lock className="size-3" />
            Nota interna
          </ModeTab>
        </div>
        {mode === 'reply' && channels.length > 0 ? (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Canal
            <Select
              aria-label="Canal da resposta"
              value={channel ? channelKey(channel) : ''}
              onChange={(e) => {
                const next = channels.find((c) => channelKey(c.channel) === e.target.value);
                if (next) setPicked(next.channel);
              }}
              className="h-7 max-w-64 py-0 text-xs"
            >
              {channels.map((c) => (
                <option key={channelKey(c.channel)} value={channelKey(c.channel)}>
                  {c.label}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        {mode === 'reply' ? startConversation : null}
        <div className="ml-auto flex items-center gap-2">{actions}</div>
      </div>

      {mode === 'note' ? (
        <NoteBody target={noteTarget} drafts={drafts} textareaRef={noteRef} onSent={onSent} />
      ) : !channel ? (
        <p className="px-3 py-4 text-sm text-muted-foreground">
          {commentBlockedReason ?? 'Nenhum canal de resposta disponível.'}
        </p>
      ) : channel.kind === 'comment' ? (
        <CommentBody
          ticketNumber={ticketNumber!}
          requestorEmail={requestorEmail}
          drafts={drafts}
          textareaRef={replyRef}
          focusOnMount={autoFocus}
          onSent={onSent}
        />
      ) : (
        <ChatBody
          chatId={channel.chatId}
          drafts={drafts}
          textareaRef={replyRef}
          focusOnMount={autoFocus}
          onSent={onSent}
        />
      )}
      {mode === 'reply' && commentBlockedReason && channel?.kind === 'chat' ? (
        <p className="px-3 pb-2 text-[11px] text-muted-foreground">{commentBlockedReason}</p>
      ) : null}
    </section>
  );
}

function ModeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-[5px] px-2.5 text-[13px] font-medium transition-colors',
        active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

function FileChips({ files, onRemove }: { files: File[]; onRemove: (index: number) => void }) {
  if (files.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {files.map((file, i) => (
        <li key={`${file.name}-${i}`} className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs">
          <Paperclip className="size-3 text-muted-foreground" />
          <span className="max-w-48 truncate">{file.name}</span>
          <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
          <button type="button" aria-label={`Remover ${file.name}`} onClick={() => onRemove(i)} className="text-muted-foreground hover:text-destructive">
            <X className="size-3" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function useFilePicker(multiple: boolean) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const input = (
    <input
      ref={inputRef}
      type="file"
      multiple={multiple}
      className="hidden"
      onChange={(e) => {
        setError(null);
        const accepted: File[] = [];
        for (const f of Array.from(e.target.files ?? [])) {
          if (f.size > MAX_UPLOAD_BYTES) setError(`"${f.name}" passa de ${MAX_LABEL}.`);
          else accepted.push(f);
        }
        setFiles((prev) => (multiple ? [...prev, ...accepted] : accepted.slice(0, 1)));
        e.target.value = '';
      }}
    />
  );
  return {
    files,
    error,
    input,
    open: () => inputRef.current?.click(),
    remove: (i: number) => setFiles((prev) => prev.filter((_, j) => j !== i)),
    reset: () => setFiles([]),
  };
}

/** Enter envia na conversa (hábito do WhatsApp); no comentário, Ctrl+Enter. */
function submitOnKey(e: KeyboardEvent<HTMLTextAreaElement>, enterSends: boolean, submit: () => void) {
  if (e.key !== 'Enter') return;
  if ((enterSends && !e.shiftKey) || e.ctrlKey || e.metaKey) {
    e.preventDefault();
    submit();
  }
}

function CommentBody({
  ticketNumber,
  requestorEmail,
  drafts,
  textareaRef,
  focusOnMount,
  onSent,
}: {
  ticketNumber: number;
  requestorEmail: string | null;
  drafts: ReturnType<typeof useDrafts>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** chegou pelo "Responder": a caixa nasce focada (mesmo montando depois) */
  focusOnMount: boolean;
  onSent: () => void;
}) {
  const add = useAddComment(ticketNumber);
  const qc = useQueryClient();
  const picker = useFilePicker(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const text = drafts.drafts.reply;
  const busy = uploading || add.isPending;

  const submit = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setError(null);
    let fileIds: number[] | undefined;
    if (picker.files.length > 0) {
      setUploading(true);
      try {
        fileIds = [];
        for (const f of picker.files) fileIds.push((await uploadTicketFile(ticketNumber, f)).id);
      } catch (e) {
        setUploading(false);
        setError(errorMessage(e));
        void qc.invalidateQueries({ queryKey: ['erp', 'tickets', 'files', { ticketNumber }] });
        return;
      }
      setUploading(false);
    }
    add.mutate(
      { body, internal: false, ...(fileIds ? { fileIds } : {}), ...(sendEmail && requestorEmail ? { sendEmail: true } : {}) },
      {
        onSuccess: () => {
          drafts.clear('reply');
          picker.reset();
          setSendEmail(false);
          onSent();
        },
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  return (
    <div className="space-y-2 px-3 py-2.5">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => drafts.set('reply', e.target.value)}
        onKeyDown={(e) => submitOnKey(e, false, () => void submit())}
        placeholder="Escreva um comentário público…"
        autoFocus={focusOnMount}
        aria-label="Resposta"
        className={textareaClass}
      />
      <FileChips files={picker.files} onRemove={picker.remove} />
      {picker.error || error ? <p className="text-xs text-destructive">{picker.error ?? error}</p> : null}
      <div className="flex flex-wrap items-center gap-3">
        {picker.input}
        <Button type="button" variant="ghost" size="sm" onClick={picker.open} disabled={busy} title={`Anexar (até ${MAX_LABEL} cada)`}>
          <Paperclip />
          Anexar
        </Button>
        {requestorEmail ? (
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(v === true)} />
            Enviar por e-mail ({requestorEmail})
          </label>
        ) : null}
        <span className="ml-auto text-[11px] text-muted-foreground/70">Ctrl+Enter envia</span>
        <Button size="sm" onClick={() => void submit()} disabled={!text.trim() || busy}>
          {busy ? <Spinner className="size-4 border-primary-foreground" /> : <SendHorizonal />}
          Enviar
        </Button>
      </div>
    </div>
  );
}

function ChatBody({
  chatId,
  drafts,
  textareaRef,
  focusOnMount,
  onSent,
}: {
  chatId: number;
  drafts: ReturnType<typeof useDrafts>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  focusOnMount: boolean;
  onSent: () => void;
}) {
  const me = useAuthUser();
  const { can, ready } = usePermissions();
  const detailQ = useQuery(chatDetailOptions(chatId));
  const chat = detailQ.data;
  const isWhatsapp = chat?.origin === 'whatsapp';
  const archived = chat?.status === 'archived';
  const windowQ = useQuery({ ...whatsappWindowOptions(chatId), enabled: isWhatsapp && !archived });
  const send = useSendChatMessage(chatId);
  const sendMedia = useSendChatMedia(chatId);
  const sendTemplate = useSendChatTemplate(chatId);
  const resume = useResumeChat();
  const { assume, pending: assuming, error: assumeError, dialog } = useAssumeWork();
  const picker = useFilePicker(false);
  const [template, setTemplate] = useState<ChatTemplateBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const text = drafts.drafts.reply;

  // Respostas rápidas (PoolTrack, 02/10): `/` na caixa ou o botão; o texto entra
  // para revisão, nunca é enviado. Só onde a caixa de resposta existe: conversa
  // minha ou na fila, aberta, e com a janela de 24h aberta.
  const canType =
    chat !== undefined &&
    chat.status !== 'archived' &&
    (chat.status === 'inbox' || (chat.status === 'in_attendance' && chat.responsible?.id === me?.id)) &&
    !(isWhatsapp && windowQ.data?.open === false);
  const quick = useQuickReplyComposer({
    chatId,
    enabled: canType,
    text,
    setText: (value) => drafts.set('reply', value),
    textareaRef,
    buttonVariant: 'label',
  });

  if (detailQ.isPending) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (!chat) return <p className="px-3 py-3 text-sm text-destructive">Não foi possível carregar a conversa.</p>;

  const mine = chat.status === 'in_attendance' && chat.responsible?.id === me?.id;
  // na FILA (de ninguém): responder é assumir — o envio assume antes e segue,
  // sem um clique a mais (SC-003: do Hoje à resposta enviada em 2 cliques).
  // A pausa (015) continua perguntando: o assumir é o de sempre.
  const queued = chat.status === 'inbox';
  /** envia como dono: na fila, assume primeiro (a API exige a posse) */
  const asOwner = (then: () => void) => {
    if (!queued) return then();
    assume({ kind: 'chat', chatId: chat.id }, then);
  };

  if (archived) {
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
        Conversa encerrada.
        <Button size="sm" variant="outline" disabled={resume.isPending} onClick={() => resume.mutate(chat.id, { onError: (e) => setError(errorMessage(e)) })}>
          Retomar atendimento
        </Button>
        {error ? <span className="text-xs text-destructive">{error}</span> : null}
      </div>
    );
  }

  if (!mine && !queued) {
    // com OUTRA pessoa: tomar a conversa é decisão explícita (chat.manage_others)
    const other = chat.responsible;
    const takeover = ready && can('chat.manage_others');
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-3 text-sm">
        <span className="text-muted-foreground">
          A conversa está com {other?.name ?? 'outra pessoa'}. Só quem a atende responde ao cliente — a nota
          interna segue livre.
        </span>
        {takeover ? (
          <Button
            size="sm"
            disabled={assuming}
            onClick={() => assume({ kind: 'chat', chatId: chat.id, takeover: true }, () => textareaRef.current?.focus())}
          >
            {assuming ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Assumir de {other?.name ?? 'outra pessoa'}
          </Button>
        ) : null}
        {assumeError ? <span className="text-xs text-destructive">{assumeError}</span> : null}
        {dialog}
      </div>
    );
  }

  // janela de 24h fechada: só um modelo aprovado reabre (a nota segue livre)
  if (isWhatsapp && windowQ.data?.open === false) {
    return (
      <div className="space-y-2 px-3 py-2.5">
        <p className="text-xs text-muted-foreground">
          Janela de 24h fechada — envie um modelo aprovado para reabrir a conversa.
        </p>
        <TemplatePicker
          idPrefix={`cmp-tpl-${chat.id}`}
          // os modelos do número da conversa (spec 025: o da Blip tem os da Blip)
          {...(chat.waPhoneNumberId ? { phoneNumberId: chat.waPhoneNumberId } : {})}
          onChange={setTemplate}
          disabled={sendTemplate.isPending}
        />
        {error || assumeError ? <p className="text-xs text-destructive">{error ?? assumeError}</p> : null}
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!template || sendTemplate.isPending || assuming}
            onClick={() =>
              template &&
              asOwner(() =>
                sendTemplate.mutate(template, {
                  onSuccess: onSent,
                  onError: (e) => setError(errorMessage(e)),
                })
              )
            }
          >
            {sendTemplate.isPending ? <Spinner className="size-4 border-primary-foreground" /> : <SendHorizonal />}
            Enviar modelo
          </Button>
        </div>
        {dialog}
      </div>
    );
  }

  const busy = send.isPending || sendMedia.isPending || assuming;
  const submit = () => {
    if (busy) return;
    setError(null);
    const file = picker.files[0];
    const value = text.trim();
    if (!file && !value) return;
    asOwner(() => {
      if (file) {
        // arquivo vai como mídia, com o texto de legenda
        sendMedia.mutate(
          { file, caption: value || undefined },
          {
            onSuccess: () => {
              picker.reset();
              drafts.clear('reply');
              onSent();
            },
            onError: (e) => setError(errorMessage(e)),
          }
        );
        return;
      }
      send.mutate(
        { text: value, internal: false },
        {
          onSuccess: () => {
            drafts.clear('reply');
            onSent();
          },
          onError: (e) => setError(errorMessage(e)),
        }
      );
    });
  };

  return (
    <div className="relative space-y-2 px-3 py-2.5">
      {quick.slashPopup}
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => {
          drafts.set('reply', e.target.value);
          quick.onTextChange(e.target);
        }}
        onKeyDown={(e) => {
          // com a lista das respostas rápidas aberta, as teclas são dela (Enter
          // escolhe e NUNCA envia)
          if (quick.handleKeyDown(e)) return;
          submitOnKey(e, true, submit);
        }}
        {...quick.textareaProps}
        placeholder={picker.files.length ? 'Legenda (opcional)…' : `Mensagem para ${chat.requestorName}… (/ para respostas rápidas)`}
        autoFocus={focusOnMount}
        aria-label="Resposta"
        className={textareaClass}
      />
      {quick.warning}
      <FileChips files={picker.files} onRemove={picker.remove} />
      {queued ? (
        <p className="text-[11px] text-muted-foreground">
          {/* spec 021: responder uma conversa "com o bot" assume e cala o bot */}
          A conversa está na fila — ao enviar, você a assume
          {chat.externalBotActive ? ' e o bot para de responder' : ''}.
        </p>
      ) : null}
      {picker.error || error || assumeError ? (
        <p className="text-xs text-destructive">{picker.error ?? error ?? assumeError}</p>
      ) : null}
      {dialog}
      <div className="flex items-center gap-3">
        {picker.input}
        <Button type="button" variant="ghost" size="sm" onClick={picker.open} disabled={busy} title={`Anexar (até ${MAX_LABEL})`}>
          <Paperclip />
          Anexar
        </Button>
        {quick.button}
        <span className="ml-auto text-[11px] text-muted-foreground/70">Enter envia · Shift+Enter quebra linha</span>
        <Button size="sm" onClick={submit} disabled={busy || (!text.trim() && picker.files.length === 0)}>
          {busy ? <Spinner className="size-4 border-primary-foreground" /> : <SendHorizonal />}
          Enviar
        </Button>
      </div>
    </div>
  );
}

function NoteBody({
  target,
  drafts,
  textareaRef,
  onSent,
}: {
  target: NoteTarget;
  drafts: ReturnType<typeof useDrafts>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onSent: () => void;
}) {
  const add = useAddComment(target.kind === 'ticket' ? target.ticketNumber : 0);
  const sendChat = useSendChatMessage(target.kind === 'chat' ? target.chatId : 0);
  const [error, setError] = useState<string | null>(null);
  const text = drafts.drafts.note;
  const busy = add.isPending || sendChat.isPending;
  // spec 037 (PR C): na nota do TICKET, o @ abre a equipe (a nota da conversa não tem menção)
  const mention = useMentionAutocomplete({
    ticketNumber: target.kind === 'ticket' ? target.ticketNumber : 0,
    enabled: target.kind === 'ticket',
    value: text,
    setValue: (v) => drafts.set('note', v),
    textareaRef,
  });

  const blocked = target.kind === 'none' ? target.reason : null;
  const submit = () => {
    const value = text.trim();
    if (!value || busy || blocked) return;
    setError(null);
    const done = {
      onSuccess: () => {
        drafts.clear('note');
        onSent();
      },
      onError: (e: unknown) =>
        setError(mentionErrorMessage((e as { error?: string } | null)?.error) ?? errorMessage(e)),
    };
    const mentions = mention.mentions;
    if (target.kind === 'ticket') add.mutate({ body: value, internal: true, ...(mentions.length > 0 ? { mentions } : {}) }, done);
    else if (target.kind === 'chat') sendChat.mutate({ text: value, internal: true }, done);
  };

  const hint = useMemo(
    () => (target.kind === 'chat' ? 'Visível somente para a equipe — fica na conversa, nunca vai ao cliente.' : 'Visível somente para a equipe.'),
    [target.kind]
  );

  return (
    <div className="space-y-2 px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-[#D48806]">
        <Lock className="size-3.5" />
        {blocked ?? hint}
      </p>
      <textarea
        ref={textareaRef}
        value={text}
        disabled={blocked !== null}
        onChange={(e) => {
          drafts.set('note', e.target.value);
          mention.onCaret(e.target);
        }}
        onKeyDown={(e) => {
          if (mention.onKeyDown(e)) return;
          submitOnKey(e, false, submit);
        }}
        onClick={(e) => mention.onCaret(e.currentTarget)}
        onKeyUp={(e) => mention.onCaret(e.currentTarget)}
        placeholder={target.kind === 'ticket' ? 'Escreva uma nota para a equipe… (@ menciona alguém)' : 'Escreva uma nota para a equipe…'}
        aria-label="Nota interna"
        className={cn(textareaClass, 'border-[#FFE58F] bg-white')}
      />
      {mention.panel}
      {mention.summary}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex items-center gap-3">
        <Label className="ml-auto text-[11px] font-normal text-muted-foreground/70">Ctrl+Enter salva</Label>
        <Button
          size="sm"
          onClick={submit}
          disabled={busy || !text.trim() || blocked !== null}
          className="bg-[#D48806] text-white hover:bg-[#AD6800]"
        >
          {busy ? <Spinner className="size-4 border-white" /> : <Lock />}
          Salvar nota interna
        </Button>
      </div>
    </div>
  );
}
